import type { ResearchSearchItem } from '@next-space/fe-api-idl';
import { QASearchSource } from '@next-space/fe-api-idl';
import { emitter, EmitterEvent } from '../../utils';
import { getToken } from '../../utils/cookie';
import { getQAQueryOption } from './hook';
import { searchPageStore } from './store';
import { EventTypes, type CreateChatParams } from './types';
import { getPageTitleInfo } from './use-search';
import { message } from '../../components/message';

// Track all running streams and their controllers
const runningStreams = new Map<string, AbortController>();

interface AIResponse {
  code?: number;
  content?: string;
  reasoning?: string;
  chatId?: string;
  questionId?: string;
  done?: boolean;
  functionCall?: string;
  searchQueries?: string[];
  searchResults?: ResearchSearchItem[];
}

class StreamProcessor {
  private decoder = new TextDecoder();
  private searchQueries: string[] = []; // 存储所有搜索查询
  private searchResults: ResearchSearchItem[] = []; // 存储搜索结果

  constructor(private onChunk: (chunk: AIResponse) => void) {}

  private handleResponse(text: string) {
    const dataChunks = text.split('data: ');
    const validChunks = dataChunks.slice(1).filter((chunk) => chunk.trim());

    // 跳过第一个元素（因为它是在第一个 'data:' 之前的内容）
    // 并过滤掉空白块
    for (const chunk of validChunks) {
      try {
        // 移除可能的结尾换行符
        const cleanedChunk = chunk.trim();
        const data = JSON.parse(cleanedChunk);
        if (data.type === EventTypes.INFO) {
          this.onChunk({ chatId: data.data.chatId, questionId: data.data.questionId });
        } else if (data.type === EventTypes.REASONING) {
          this.onChunk({ reasoning: data.data } as AIResponse);
        } else if (data.type === EventTypes.CONTENT) {
          this.onChunk({ content: data.data } as AIResponse);
        } else if (data.type === EventTypes.ERROR) {
          if (data.data.code === 1000) {
            emitter.emit(EmitterEvent.chatAiLimit);
            throw new Error(data.content || 'AI response error');
          } else {
            if (data.data.content) {
              message.warning(data.data.content);
            }
          }
        } else if (data.type === EventTypes.FUNCTION_CALL) {
          if (data.data instanceof Array) {
            const functionCall = (data.data as any[])
              .map((v) => {
                if (v.description) {
                  return v.description.replaceAll('...', '');
                }
                return v.name;
              })
              .filter((v) => v)
              .join('');
            this.onChunk({ functionCall } as AIResponse);
          }
        } else if (data.type === EventTypes.RESEARCH_SEARCH_START) {
          // 处理搜索开始事件
          const { searchQuery } = data.data;

          // 将查询添加到查询数组中
          if (!this.searchQueries.includes(searchQuery)) {
            this.searchQueries.push(searchQuery);
          }

          // 发送更新后的查询数组
          this.onChunk({
            searchQueries: [...this.searchQueries],
          } as AIResponse);
        } else if (data.type === EventTypes.RESEARCH_SEARCH_COMPLETE) {
          // 处理搜索完成事件
          const completeItem: ResearchSearchItem = {
            searchQuery: data.data.searchQuery,
            queryIndex: data.data.queryIndex,
            sources: data.data.sources,
          };

          // 更新对应索引的搜索结果
          const existingIndex = this.searchResults.findIndex(
            (item) => item.queryIndex === completeItem.queryIndex
          );

          if (existingIndex === -1) {
            this.searchResults.push(completeItem);
          } else {
            this.searchResults[existingIndex] = {
              ...this.searchResults[existingIndex],
              ...completeItem,
            };
          }

          // 发送更新后的搜索结果数组
          this.onChunk({
            searchResults: [...this.searchResults],
          } as AIResponse);
        } else if (data.type === EventTypes.DONE) {
          //done
        } else {
          //nothing
        }
        if (data.code === 1000) {
          emitter.emit(EmitterEvent.chatAiLimit);
          throw new Error(data.content || 'AI response error');
        }
      } catch (error) {
        console.error('Error parsing response:', error);
      }
    }
  }

  public async processStream(reader: ReadableStreamDefaultReader<Uint8Array>) {
    try {
      let done = false; // 初始化 done 状态
      while (!done) {
        const { done: isDone, value } = await reader.read();
        done = isDone; // 更新 done 状态

        if (done) break;

        const text = this.decoder.decode(value, { stream: true });
        // 直接处理读取到的文本
        this.handleResponse(text);
      }
    } catch (error) {
      console.error('Error streaming AI response:', error);
    } finally {
      reader.releaseLock();
    }
  }
}
export const exchangeStreams = (fakeId: string, newId: string) => {
  const controller = runningStreams.get(fakeId);
  if (controller) {
    runningStreams.delete(fakeId);
    runningStreams.set(newId, controller);
  }
};

// Utility function to make real API streaming call
export const streamAIResponse = async (
  getId: () => string,
  conversationId: string,
  params: CreateChatParams & {
    questionId?: string;
  },
  onChunk: (chunk: AIResponse) => void
) => {
  const controller = new AbortController();
  runningStreams.set(getId(), controller);
  const searchBlocks = params.qaQueryOption?.searchBlocks?.map((v) => {
    const block = searchPageStore.getState().pages[v.uuid ?? ''];
    const name = getPageTitleInfo(block).title;
    return { ...v, name };
  });
  const body = {
    spaceId: params.spaceId,
    question: params.keywords,
    questionId: params.questionId,
    conversationId,
    qaQueryOption: { ...getQAQueryOption(params.qaQueryOption), searchBlocks },
  };
  if (
    body.qaQueryOption.networkSearch &&
    body.qaQueryOption.qaSearchSource === QASearchSource.ALL
  ) {
    console.warn('networkSearch is true and qaSearchSource is All');
    //这个case可能不知道哪里触发的，偶现过一次，咱们遇到就纠正一下
    if (body.qaQueryOption.searchBlocks && body.qaQueryOption.searchBlocks.length > 0) {
      body.qaQueryOption.networkSearch = false;
      body.qaQueryOption.qaSearchSource = QASearchSource.NONE;
    } else {
      body.qaQueryOption.qaSearchSource = QASearchSource.AI_MODEL;
    }
  }
  try {
    const response = await fetch(`/api/rag/retrieval`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        authorization: `Bearer ${getToken()}`,
      },
      body: JSON.stringify(body),
      signal: controller.signal,
    });

    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`);
    }

    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error('Response body is not readable');
    }
    //@ts-ignore type check
    const processor = new StreamProcessor(onChunk);
    await processor.processStream(reader);
  } catch (error) {
    console.error('Error streaming AI response:', error);
  } finally {
    runningStreams.delete(getId());
  }
};

// Stop a specific stream by ID
export const stopStream = (id: string) => {
  const controller = runningStreams.get(id);
  if (controller) {
    controller.abort();
    runningStreams.delete(id);
  }
};
export const stopAllStream = () => {
  runningStreams.forEach((v) => {
    v.abort();
  });
  runningStreams.clear();
};
